import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { printReceipt } from "@/lib/printReceipt";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, FileText, Printer, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const menuCls = "bg-[#121212] border-white/10 text-white rounded-none";

const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
};

export default function Receipts() {
  const navigate = useNavigate();
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const profile = profiles.find((p) => p.id === profileId);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/receipts?profile_id=${profileId}`);
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load receipts");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (!q) return true;
      return `${r.receipt_no} ${r.party_name} ${r.purpose} ${r.amount}`.toLowerCase().includes(q);
    });
  }, [items, query, kindFilter]);

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.receipt_no || "this receipt"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (!profileId) {
    return <div className="px-4 sm:px-6 lg:px-10 py-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">No business profile available yet.</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Receipts</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Receipts &amp; Vouchers</h1>
          <p className="text-neutral-500 text-sm mt-3">Print a payment receipt for money you received, or a payment voucher for money you paid.</p>
        </div>
        {profiles.length > 1 && (
          <Select value={profileId} onValueChange={setActiveProfileId}>
            <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>
              {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">— Create new</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-4">
        <button data-testid="new-receipt-btn" onClick={() => navigate("/receipts/new?kind=receipt")} className="bg-[#121212] hover:bg-[#181818] text-left p-6 transition-colors group">
          <Receipt className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
          <div className="font-serif text-lg text-white group-hover:text-[#D4AF37] transition-colors">Payment Receipt</div>
          <div className="font-mont text-[11px] text-neutral-500 mt-2 leading-relaxed">Money received — from a client or anyone else.</div>
        </button>
        <button data-testid="new-voucher-btn" onClick={() => navigate("/receipts/new?kind=voucher")} className="bg-[#121212] hover:bg-[#181818] text-left p-6 transition-colors group">
          <FileText className="w-5 h-5 text-[#D4AF37] mb-4" strokeWidth={1.5} />
          <div className="font-serif text-lg text-white group-hover:text-[#D4AF37] transition-colors">Payment Voucher</div>
          <div className="font-mont text-[11px] text-neutral-500 mt-2 leading-relaxed">Money paid — to a vendor, worker or advance.</div>
        </button>
      </div>
      <p className="font-mont text-[11px] text-neutral-600 mb-12">
        Shortcut: open <Link to="/finance" className="text-[#D4AF37] hover:underline">Finance → Transactions</Link> and click the receipt icon on any payment — the details are filled in for you.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Saved ({items.length})</div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont h-9 w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent className={menuCls}>
              <SelectItem value="all" className="font-mont text-sm">All</SelectItem>
              <SelectItem value="receipt" className="font-mont text-sm">Receipts</SelectItem>
              <SelectItem value="voucher" className="font-mont text-sm">Vouchers</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-neutral-600 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, number, amount…" className="pl-9 bg-transparent border border-white/20 rounded-none text-white font-mont h-9" />
          </div>
        </div>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {loading ? (
          <div className="py-16 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            {items.length === 0 ? "No receipts yet — create one above" : "Nothing matches your search"}
          </div>
        ) : (
          filtered.map((r) => (
            <div key={r.id} data-testid={`receipt-row-${r.id}`} className="flex items-center justify-between gap-3 px-4 py-4 hover:bg-white/[0.02]">
              <button onClick={() => navigate(`/receipts/${r.id}`)} className="flex-1 min-w-0 text-left">
                <div className="text-white text-sm truncate">{r.party_name || "(no name)"}</div>
                <div className="font-mont text-[10px] text-neutral-500 truncate mt-0.5">
                  {r.receipt_no} · {r.kind === "voucher" ? "Voucher" : "Receipt"}{r.date && ` · ${fmtDate(r.date)}`}{r.created_by_name && ` · by ${r.created_by_name}`}
                </div>
              </button>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className={`font-mont text-sm ${r.kind === "voucher" ? "text-red-400" : "text-emerald-400"}`}>{fmtINR(r.amount)}</span>
                <button title="Print / save as PDF" onClick={() => printReceipt(r, profile)} className="text-neutral-500 hover:text-white"><Printer className="w-4 h-4" strokeWidth={1.5} /></button>
                <button title="Delete" onClick={() => remove(r)} className="text-neutral-500 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
